import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { useCameraFrameStore, useLayoutStore } from '@/store'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import CategoryFilter from './CategoryFilter.vue'
import { delay } from '@/utils'
import { isIOS } from '@/utils/device'
import { getNetworkType } from '@/utils/network'


export default /*@__PURE__*/_defineComponent({
  __name: 'DefaultCameraFrame',
  setup(__props) {

const layoutStore = useLayoutStore()
const { hasManualLoading } = storeToRefs(layoutStore)
const cameraFrameStore = useCameraFrameStore()
const { cameraMode, isDefaultCameraActive, isCameraLoading } =
  storeToRefs(cameraFrameStore)

const visible = ref(false)
const withMask = ref(false)
const isCameraHidden = computed(() => {
  return isCameraLoading.value || hasManualLoading.value
})

const timer = ref<number>()
watch(
  cameraMode,
  mode => {
    if (mode === 'try-on') {
      withMask.value = true
      clearTimeout(timer.value)
      timer.value = setTimeout(() => {
        visible.value = false
        withMask.value = false
      }, 300)
    } else {
      clearTimeout(timer.value)
      timer.value = setTimeout(() => {
        withMask.value = false
        visible.value = isDefaultCameraActive.value
      }, 0)
    }
  },
  {
    immediate: true
  }
)

const camera = ref<HTMLVideoElement>()
const stream = ref<MediaStream>()
const loaded = ref(false)

const play = async () => {
  if (visible.value) {
    // console.log('[[ Default Camera ] To Play Video ]', Date.now())
    try {
      await camera.value?.play()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log(
        '[[ Default Camera ] Play Video Error]',
        error?.message || String(error)
      )
    }
  }
}

const pause = async () => {
  console.log('[[ Default Camera ] To Pause Video ]', Date.now())
  camera.value?.pause()
}

const loadAndPlay = async () => {
  if (loaded.value) return play()
  // console.log('[[ Default Camera ] To Load Media ]', Date.now())
  try {
    const mediaStream = await cameraFrameStore.getUserMedia()
    if (camera.value && 'srcObject' in camera.value) {
      console.log(
        '[[ Default Camera ] GotUserMedia and SetSrcObject ]',
        Date.now()
      )
      stream.value = mediaStream
      camera.value.srcObject = stream.value
      play()
    }
  } catch (error) {
    console.error('[[ Default Camera ] Request Permission Failed ]')
  }
}

const unload = () => {
  if (stream.value) {
    // console.log('[[ Default Camera ] To Unload Media ]', Date.now())
    const tracks = stream.value.getTracks() || []
    tracks.forEach(track => {
      track.stop() // Stop each track
    })
  }
  stream.value = undefined
  loaded.value = false
}

const onLoadedMetaData = async () => {
  console.log('[[ Default Camera ] OnLoadedMetaData ]', Date.now())
  if (loaded.value) return
  loaded.value = true
  await delay(300)
  cameraFrameStore.setDefaultCameraReady()
}

const onError = () => {
  console.log('[[ Default Camera ] On Video Error ]', Date.now())
}

const onSuspend = async () => {
  if (visible.value) {
    // console.log('[[ Default Camera ] On Video Suspend ]')
    try {
      if (isIOS) {
        await getNetworkType()
        await camera.value?.play()
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log(
        '[[ Default Camera ] Fix Playing Error ]',
        error?.message || String(error)
      )
    }
  }
}

const onWaiting = () => {
  if (visible.value) {
    // console.log('[[ Default Camera ] On Video Waiting ]')
    play()
  }
}

const onEvent = (e: Event) => {
  console.log(`[[ Default Camera ] On Video Event / ${e.type} ]`, Date.now())
}

const onVisibilityChange = () => {
  if (visible.value) {
    if (document.visibilityState === 'visible') {
      loadAndPlay()
    } else {
      pause()
    }
  }
}

watch(
  visible,
  () => {
    if (visible.value) {
      loadAndPlay()
    } else {
      unload()
    }
  },
  {
    immediate: true
  }
)

onMounted(() => {
  // console.log('[[ Default Camera ] On Default Mounted ]', Date.now())
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  // console.log('[[ Default Camera ] On Default Unmounted ]', Date.now())
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      'default-camera-frame',
      withMask.value ? 'mask' : undefined,
      isCameraHidden.value ? 'camera-hidden' : undefined
    ])
  }, [
    _createElementVNode("video", {
      ref_key: "camera",
      ref: camera,
      class: "video",
      playsinline: "",
      onLoadedmetadata: onLoadedMetaData,
      onError: onError,
      onSuspend: onSuspend,
      onWaiting: onWaiting,
      onPause: onEvent,
      onEnded: onEvent,
      onInvalid: onEvent,
      onAbort: onEvent
    }, null, 544),
    _createVNode(CategoryFilter)
  ], 2)), [
    [_vShow, visible.value]
  ])
}
}

})